import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import type {
  AdminOrgResponse,
  AdminUserResponse,
} from "../generated/api/types.gen";
import { useAdminOrgs } from "../hooks/useAdminOrgs";
import { useAdminUsers } from "../hooks/useAdminUsers";

type AdminUser = AdminUserResponse;
type AdminOrg = AdminOrgResponse;
type AdminTab = "users" | "orgs";

export const Route = createFileRoute("/admin")({
  component: AdminPage,
});

interface BillingExemptToggleProps {
  isExempt: boolean;
  isToggling: boolean;
  onToggle: () => void;
  label: string;
}

function BillingExemptToggle({
  isExempt,
  isToggling,
  onToggle,
  label,
}: BillingExemptToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isExempt}
      aria-label={label}
      aria-busy={isToggling}
      onClick={onToggle}
      disabled={isToggling}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
        isExempt
          ? "bg-[var(--accent-primary)]"
          : "bg-[var(--bg-field)] border border-[var(--border-primary)]"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
          isExempt ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

interface AdminUserTableProps {
  users: AdminUser[];
  togglingUsers: Set<string>;
  onToggleBillingExempt: (user: AdminUser) => void;
}

function AdminUserTable({
  users,
  togglingUsers,
  onToggleBillingExempt,
}: AdminUserTableProps) {
  return (
    <div className="rounded-lg border border-[var(--border-primary)] overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-[var(--bg-field)] border-b border-[var(--border-primary)]">
            <th className="text-left px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
              User
            </th>
            <th className="text-left px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
              Role
            </th>
            <th className="text-left px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
              Billing Exempt
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr
              key={user.clerk_user_id}
              className="border-b border-[var(--border-primary)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
            >
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-[var(--text-primary)]">
                  {[user.first_name, user.last_name]
                    .filter(Boolean)
                    .join(" ") || "No name"}
                </div>
                <div className="text-xs text-[var(--text-secondary)]">
                  {user.email || "No email"}
                </div>
              </td>
              <td className="px-4 py-3">
                {user.role ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--accent-primary)]/15 text-[var(--accent-primary)]">
                    {user.role}
                  </span>
                ) : (
                  <span className="text-xs text-[var(--text-tertiary)]">
                    --
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <BillingExemptToggle
                  isExempt={user.billing_exempt}
                  isToggling={togglingUsers.has(user.clerk_user_id)}
                  onToggle={() => onToggleBillingExempt(user)}
                  label={`Billing exempt for ${user.email || "user"}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface AdminOrgTableProps {
  orgs: AdminOrg[];
  togglingOrgs: Set<string>;
  onToggleBillingExempt: (org: AdminOrg) => void;
}

function AdminOrgTable({
  orgs,
  togglingOrgs,
  onToggleBillingExempt,
}: AdminOrgTableProps) {
  return (
    <div className="rounded-lg border border-[var(--border-primary)] overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-[var(--bg-field)] border-b border-[var(--border-primary)]">
            <th className="text-left px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
              Organization
            </th>
            <th className="text-left px-4 py-3 text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
              Billing Exempt
            </th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((org) => (
            <tr
              key={org.org_id}
              className="border-b border-[var(--border-primary)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
            >
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-[var(--text-primary)]">
                  {org.name}
                </div>
                <div className="text-xs text-[var(--text-secondary)]">
                  {org.slug || org.org_id}
                </div>
              </td>
              <td className="px-4 py-3">
                <BillingExemptToggle
                  isExempt={org.billing_exempt}
                  isToggling={togglingOrgs.has(org.org_id)}
                  onToggle={() => onToggleBillingExempt(org)}
                  label={`Billing exempt for ${org.name}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);
  return debounced;
}

function UsersTab({ query }: { query: string }) {
  const {
    users,
    totalCount,
    isLoading,
    errorMessage,
    togglingUsers,
    toggleBillingExempt,
  } = useAdminUsers(query);

  return (
    <AdminTabContent
      isLoading={isLoading}
      errorMessage={errorMessage?.message ?? null}
      count={totalCount}
      noun="user"
      isEmpty={users.length === 0}
      hasQuery={query.length > 0}
    >
      <AdminUserTable
        users={users}
        togglingUsers={togglingUsers}
        onToggleBillingExempt={toggleBillingExempt}
      />
    </AdminTabContent>
  );
}

function OrgsTab({ query }: { query: string }) {
  const {
    orgs,
    totalCount,
    isLoading,
    errorMessage,
    togglingOrgs,
    toggleBillingExempt,
  } = useAdminOrgs(query);

  return (
    <AdminTabContent
      isLoading={isLoading}
      errorMessage={errorMessage?.message ?? null}
      count={totalCount}
      noun="organization"
      isEmpty={orgs.length === 0}
      hasQuery={query.length > 0}
    >
      <AdminOrgTable
        orgs={orgs}
        togglingOrgs={togglingOrgs}
        onToggleBillingExempt={toggleBillingExempt}
      />
    </AdminTabContent>
  );
}

interface AdminTabContentProps {
  isLoading: boolean;
  errorMessage: string | null;
  count: number;
  noun: string;
  isEmpty: boolean;
  hasQuery: boolean;
  children: React.ReactNode;
}

function AdminTabContent({
  isLoading,
  errorMessage,
  count,
  noun,
  isEmpty,
  hasQuery,
  children,
}: AdminTabContentProps) {
  return (
    <>
      {errorMessage && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {errorMessage}
        </div>
      )}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--text-primary)]" />
        </div>
      )}
      {!(isLoading || isEmpty) && (
        <>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            {count} {noun}
            {count !== 1 ? "s" : ""} found
          </p>
          {children}
        </>
      )}
      {!isLoading && isEmpty && !errorMessage && (
        <div className="text-center py-12">
          <p className="text-[var(--text-secondary)]">
            {hasQuery
              ? `No ${noun}s found matching your search.`
              : `No ${noun}s found.`}
          </p>
        </div>
      )}
    </>
  );
}

function AdminPage() {
  const [tab, setTab] = useState<AdminTab>("users");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);

  return (
    <div className="min-h-screen bg-[var(--bg-default)]">
      <Header currentPage="admin" />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-semibold text-[var(--text-primary)] mb-6">
          Admin
        </h1>
        <div className="flex gap-2 mb-6">
          {(["users", "orgs"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === t
                  ? "bg-[var(--accent-primary)] text-white"
                  : "bg-[var(--bg-field)] text-[var(--text-secondary)] border border-[var(--border-primary)]"
              }`}
            >
              {t === "users" ? "Users" : "Organizations"}
            </button>
          ))}
        </div>
        <div className="mb-6">
          <input
            type="text"
            placeholder={
              tab === "users"
                ? "Search by name or email..."
                : "Search by name or slug..."
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full max-w-md px-4 py-2.5 rounded-lg bg-[var(--bg-field)] border border-[var(--border-primary)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)] transition-colors"
          />
        </div>
        {tab === "users" ? (
          <UsersTab query={debouncedQuery} />
        ) : (
          <OrgsTab query={debouncedQuery} />
        )}
      </main>
    </div>
  );
}
